import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import TemplateViewerClient from "components/proposals/proposalTemplates/templateViewerClient";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { fetchLogoSrc, fetchRelations } from "../../../assets/js/common";
import { getDomainConfig } from "../../../config/domainConfig";

const ProposalEmailValidation = () => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const [formData, setFormData] = useState(undefined);
  const [logoSrc, setLogoSrc] = useState(null);
  const [customerId, setCustomerId] = useState(null);
  const handleClose = () => setShowModal(false);
  const domainCfg = getDomainConfig();

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      const guid = location.pathname.split("/").pop();

      try {
        // 1. Fetch ProposalDetails
        const proposalDetailsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/proposalDetailsRoute/getProposalDetailsById/${guid}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );
        const proposalDetails = proposalDetailsRes.data;

        if (!proposalDetails) {
          setFormData(null);
          setIsLoading(false);
          return;
        }

        // 2. Fetch related Proposal
        const proposalRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/proposalRoute/getProposalById/${proposalDetails.ProposalId}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );
        const proposal = proposalRes.data;
        setCustomerId(proposal.CustomerId);
        sessionStorage.setItem("customerId", proposal.CustomerId);

        const proposalVersionId = proposalDetails.ProposalVersionId;

        // 3. Fetch related User (admin)
        const userRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${proposalDetails.CommunicationUserId}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );
        const userData = userRes.data;

        // 4. Fetch related Relation (client)
        const relationData = await fetchRelations(proposalDetails.RelationId);

        // 5. Fetch related Location
        const locationRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/locationRoute/getById/${proposalDetails.LocationId}`, {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
        );
        const locationData = locationRes.data;

        const settingsRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/settingsRoute/getSettingsBranding/${proposal.CustomerId}`,
          {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret,
            },
          }
        );
        const settings = settingsRes.data;

        // 6. Fetch proposal tasks and build selectedSubtasks
        const tasksRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/proposalRoute/${proposalDetails.ProposalId}/tasks`,
          {
            params: proposalVersionId
              ? { proposalVersionId }
              : {},
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret,
            },
          }
        );
        const proposalTasks = tasksRes.data || [];

        const enrichedTasks = [];
        const selectedSubtasksArray = [];

        for (const task of proposalTasks) {
          try {
            const subtasksRes = await axios.get(
                `${process.env.REACT_APP_API_URL}/proposalRoute/proposalTasks/${task.ProposalTaskId}/subtasks`, {
                headers: {
                  Authorization: process.env.REACT_APP_BasicAuthSecret
                }
              }
              );

            const allSubtasks = subtasksRes.data || [];
            const selectedSubtasks = allSubtasks.filter((sub) => sub.selected !== false);

            enrichedTasks.push({
              ...task,
              Subtasks: allSubtasks,
            });

            selectedSubtasks.forEach((sub) => {
              selectedSubtasksArray.push({
                proposalTaskId: task.ProposalTaskId,
                taskId: task.TaskId,
                ...(sub.isCustom ? { subtaskId: sub.id || sub.SubtaskId, subtaskName: sub.name || sub.Subtask, isCustom: true } : { subtaskId: sub.id || sub.SubtaskId }),
                selectedDays: task.SelectedDays
                  ? JSON.parse(task.SelectedDays)
                  : [],
                monthlyDay: task.MonthlyDay || null,
              });
            });
          } catch (err) {
            console.error("Error fetching subtasks:", err);
          }
        }

        // 7. Combine all into formData
        setFormData({
          proposalDetailsData: {
            ...proposalDetails,
            ProposalVersionId: proposalVersionId,
          },
          proposal,
          userData,
          relationData,
          locationData,
          settings,
          selectedSubtasks: selectedSubtasksArray,
          finLabels: domainCfg.financialLabels,
          preloadedTasks: enrichedTasks,
          preloadedAllSubtasksByTaskId: Object.fromEntries(
            enrichedTasks.map((t) => [t.TaskId, t.Subtasks])
          ),
        });

        setShowModal(true);
      } catch (error) {
        console.error("Error loading proposal data:", error);
        setFormData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [location.pathname]);

  useEffect(() => {
    const getLogo = async () => {
      if (customerId) {
        const src = await fetchLogoSrc(customerId);
        setLogoSrc(src);
      }
    };

    getLogo();
  }, [customerId]);

  return (
    <>
      {isLoading || formData === undefined ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status" />
        </div>
      ) : formData ? (
        <Modal
          size="lg"
          show={showModal}
          onHide={handleClose}
          backdrop="static"
        >
          <Modal.Header>
            <Modal.Title>
              {formData?.proposalDetailsData?.TemplateName}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <TemplateViewerClient
              formData={formData}
              workType={formData?.proposalDetailsData?.WorkType}
              logoSrc={logoSrc}
            />
          </Modal.Body>
        </Modal>
      ) : (
        <div className="text-center my-5">
          <span>{t("propodalNotExist")}</span>
        </div>
      )}
    </>
  );
};

export default ProposalEmailValidation;
